import React from 'react'
import {
  CircularProgress,
  Button,
  TextField,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
  Link,
  Box,
} from '@mui/material'
import { size } from 'lodash'
import useComponentSize from '@rehooks/component-size'
import { useSelector } from 'react-redux'
import { useContentHeight, useHandleChangeRoute } from 'data/hooks'
import { useWidth, useLandscape } from 'data/layout/actions'
import { useStyles } from 'common/styles'
import { useHandleSendSupportEmail } from 'data/contact/actions'
import { validateEmail } from 'data/helpers'

export default function Support() {
  const buttonRef = React.useRef(null)
  const ref = React.useRef(null)
  const width = useWidth()
  const landscape = useLandscape()
  let componentSize = useComponentSize(ref)
  const marginTop = (useContentHeight() - (componentSize.height + 8)) / 2
  const classes = useStyles({ landscape, marginTop, width })

  const { supportFormSubmitted } = useSelector(state => state.support)

  const [values, setValues] = React.useState({ firstName: null, lastName: null, email: null })
  const [btnDisabled, setBtnDisabled] = React.useState(true)
  const [{ errors, loading }, setState] = React.useState({
    errors: {},
    loading: false,
  })

  const handleChangeRoute = useHandleChangeRoute()
  const handleSendSupportEmail = useHandleSendSupportEmail()

  const handleChange = React.useCallback(
    name => e => {
      const value = e && e.target && e.target.value
      if (name === 'email') {
        if (!validateEmail(value)) {
          errors.email = 'Invalid email format.'
        } else {
          delete errors.email
        }
      }
      setValues(prev => ({ ...prev, [name]: value }))
      setState(prev => ({ ...prev, errors: errors }))
    },
    [setState, setValues],
  )

  const handleBlur = () => {
    if (!values.email) {
      setBtnDisabled(true)
      errors.email = 'Email is Required'
    } else if (values.email) {
      delete errors.email
    }
    if (!values.firstName) {
      setBtnDisabled(true)
      errors.firstName = 'First name is Required'
    } else if (values.firstName) {
      delete errors.firstName
    }
    if (!values.lastName) {
      setBtnDisabled(true)
      errors.lastName = 'Last name is Required'
    } else if (values.lastName) {
      delete errors.lastName
    }
    if (size(errors) === 0) {
      setBtnDisabled(false)
    }
    setState(prev => ({ ...prev, errors: errors }))
  }

  const theme = useTheme()
  const { layout } = theme

  // Header
  const header = (
    <Box sx={{
      width: '100%',
      backgroundColor: theme.palette.background.dark,
      borderRadius: '0px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      mb: 4,
      pt: 0,
      pb: { xs: 3, md: 4 },
      px: 0,
    }}>
      <Box sx={{ maxWidth: '91.666667%', mx: 'auto', p: { xs: 2, md: 3 } }}>
        <Grid container spacing={0} sx={{ px: 0 }}>
          <Grid item xs={12} md={8} sx={{ pl: 0 }}>
            <Typography variant='h5' sx={{ color: 'white', fontWeight: 'bold', mb: 1, pl: 0 }}>
              Support
            </Typography>
            <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: { xs: 2, md: 0 }, whiteSpace: 'normal', wordBreak: 'normal', pl: 0 }}>
              Get help and contact our support team.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )

  if (supportFormSubmitted) {
    return (
      <>
        {header}
        <Grid container justifyContent='center' alignItems='center' sx={{ minHeight: '40vh', mt: 4 }}>
          <Paper
            elevation={4}
            sx={{
              padding: 3,
              maxWidth: { xs: '90%', md: 600 },
              margin: 'auto',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
            }}
          >
            <Typography align='center' variant='h4' gutterBottom>
              Thank You!
            </Typography>
            <Typography align='center' variant='body2' gutterBottom>
              Your support request has been received. We'll get back to you within 24 hours.
            </Typography>
            <Divider sx={{ marginY: 2 }} />
            <Typography align='center' variant='body1'>
              <Link onClick={handleChangeRoute('/')} sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                Go to the home page
              </Link>
            </Typography>
          </Paper>
        </Grid>
      </>
    )
  } else {
    return (
      <>
        {header}
        <Paper ref={ref} className={classes.supportContainer}>
          <Grid container alignItems='center' spacing={1}>
            <Grid item xs={12}>
              <Typography align='center' variant='h5'>
                Contact MyStethi's Support Team
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
          <Grid container alignItems='center' rowSpacing={2} direction='column'>
            {loading && (
              <CircularProgress size={layout.progressSize} className={classes.submitProgress} />
            )}
            <Grid item>
              <Typography variant='body1'>
                <p>
                  Having technical issues? Email us at{' '}
                  <a href={'mailto:support@mystethi.com?subject=MyStethi Support'}>
                    support@mystethi.com
                  </a>
                  {` `}or fill out the form below and we will get back to you within 24 hours.
                </p>
              </Typography>
              <TextField
                variant='outlined'
                id='email'
                label='Email'
                required
                placeholder='Enter your email'
                type='email'
                fullWidth
                onChange={handleChange('email')}
                onBlur={handleBlur}
                error={errors['email'] !== undefined}
                // Forces a constant helper text margin
                helperText={errors['email'] || ' '}
                disabled={loading}
              />
              <TextField
                variant='outlined'
                id='firstName'
                label='First Name'
                required
                fullWidth
                placeholder='First Name'
                onBlur={handleBlur}
                onChange={handleChange('firstName')}
                error={errors['firstName'] !== undefined}
                // Forces a constant helper text margin
                helperText={errors['firstName'] || ' '}
                disabled={loading}
              />
              <TextField
                variant='outlined'
                id='lastName'
                label='Last Name'
                required
                fullWidth
                placeholder='Last Name'
                onBlur={handleBlur}
                onChange={handleChange('lastName')}
                error={errors['lastName'] !== undefined}
                // Forces a constant helper text margin
                helperText={errors['lastName'] || ' '}
                disabled={loading}
              />
              <TextField
                id='message'
                required
                label='More information'
                placeholder='Details around the issue you are experiencing'
                multiline
                rows={3}
                maxRows={10}
                variant='outlined'
                onBlur={handleBlur}
                onChange={handleChange('message')}
                fullWidth
                error={errors['message'] !== undefined}
                helperText={errors['message'] || ' '}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={6}>
              <Button
                ref={buttonRef}
                variant='contained'
                color='secondary'
                onClick={handleSendSupportEmail({ values, setState })}
                disabled={btnDisabled}
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </>
    )
  }
}
